/* eslint-disable no-unsafe-optional-chaining */
import {
	AreaRefTableNameEnum,
	EUpdateAreaEnum,
	TableTypes,
	UpdateHistoryRefTableNameEnum,
} from '@/constant';
import { useGetListData } from '@/hooks';
import { CommonActions, CommonSelectors, useAppSelector } from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import { arrayToJson } from '@/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { DefaultOptionType } from 'antd/es/select';

import { TFilterWithViewAllRef } from '../common/filter-with-view-all/type';
import { getDocuments } from '../define-document-area/define-document-area.config';
import {
	getColumns,
	updateAreaAllOptions,
	updateAreaOptionsMap,
	updateHistoryOptionsMap,
} from './config';
import { IUpdateAreaOptions, IUpdateHistoryProps } from './type';

export const useLogic = ({
	refTableName,
	id: propsId,
	optionalFields = [],
	areaRefTableName,
}: IUpdateHistoryProps) => {
	const { id } = useParams<{ id: string }>();
	const filterRef = useRef<TFilterWithViewAllRef>(null);
	const { isForeignRole } = useSelector(MyPageSelectors.selectDataMyPage);
	const { t } = useTranslation(['updateHistory', 'national']);
	const {
		list: dataSource,
		fetching: loading,
		total,
		params: reduxParams,
	} = useGetListData({
		type: TableTypes.UPDATE_HISTORY,
	});
	const autocompleteSuggestion = useAppSelector(
		CommonSelectors.selectAutocompleteSuggestion
	);

	useEffect(() => {
		if (
			refTableName === UpdateHistoryRefTableNameEnum.UNAUTHORIZED_DEPARTURE ||
			refTableName === UpdateHistoryRefTableNameEnum.ASSIGNMENT_FRANCHISE
		) {
			dispatch(CommonActions.getAutocompleteSuggestionRequest());
		}
	}, [refTableName]);

	const baseDocuments = useMemo(
		() => getDocuments(refTableName as any, isForeignRole),
		[refTableName, isForeignRole]
	);

	const historyUpdateOptions = useMemo(() => {
		const baseOpts = updateHistoryOptionsMap[refTableName] || [];
		const filteredBaseOpts = isForeignRole
			? baseOpts.filter((option) =>
					option?.isForeignRole ? option?.isForeignRole : true
				)
			: baseOpts;
		const hasOptionalFields = !!optionalFields?.length;
		const optionalOpts = hasOptionalFields
			? optionalFields?.map((op) => ({ value: op.label, label: op.label }))
			: [];
		switch (reduxParams?.fieldArea) {
			case EUpdateAreaEnum.DOCUMENT_INFO:
				return updateAreaAllOptions.concat(
					baseDocuments as IUpdateAreaOptions[]
				);
			case EUpdateAreaEnum.BASIC_INFO: {
				const filteredOpts = isForeignRole
					? filteredBaseOpts
					: baseOpts.filter((option) => !option?.type);
				return [...filteredOpts, ...optionalOpts];
			}
			case EUpdateAreaEnum.SEAL_INFO: {
				const sealOpts = baseOpts.filter(
					(option) => option?.type === EUpdateAreaEnum.SEAL_INFO
				);
				return [...updateAreaAllOptions, ...sealOpts];
			}
			case EUpdateAreaEnum.NOTIFICATION_CONTROL: {
				const notificationOpts = baseOpts.filter(
					(option) => option?.type === EUpdateAreaEnum.NOTIFICATION_CONTROL
				);
				return [...updateAreaAllOptions, ...notificationOpts];
			}
			case EUpdateAreaEnum.NAME_PLATE_INFO: {
				const namePlateOpts = baseOpts.filter(
					(option) => option?.type === EUpdateAreaEnum.NAME_PLATE_INFO
				);
				return [...updateAreaAllOptions, ...namePlateOpts];
			}
			default:
				return [...filteredBaseOpts, ...optionalOpts, ...baseDocuments];
		}
	}, [reduxParams?.fieldArea, refTableName, optionalFields, baseDocuments]);

	const updateAreaOptions = useMemo(() => {
		const newUpdateAreaOptions = areaRefTableName
			? updateAreaOptionsMap?.[areaRefTableName as AreaRefTableNameEnum]
			: [];
		return newUpdateAreaOptions;
	}, [areaRefTableName, t]);

	const historyUpdateOptionJson = useMemo(
		() => arrayToJson(historyUpdateOptions as any, 'value'),
		[historyUpdateOptions]
	);

	const updateAreaOptionsJson = useMemo(
		() => arrayToJson(updateAreaOptions, 'value'),
		[updateAreaOptions]
	);

	const dispatch = useDispatch();

	const initialValues = useMemo(() => {
		return {
			...reduxParams,
			fieldName: '',
			fieldArea: '',
		};
	}, []);

	useEffect(() => {
		if (!propsId && !id) return;

		fetchData({});
	}, [propsId, id]);

	const convertRefTableNameToCallAPI = useCallback(
		(refTableName: UpdateHistoryRefTableNameEnum) => {
			switch (refTableName) {
				case UpdateHistoryRefTableNameEnum.FOREIGN_UNIT:
					return UpdateHistoryRefTableNameEnum.UNIT;
				case UpdateHistoryRefTableNameEnum.PUBLIC_ASSIGNMENT_FRANCHISE:
					return UpdateHistoryRefTableNameEnum.ASSIGNMENT_FRANCHISE;

				default:
					return refTableName;
			}
		},
		[]
	);

	const fetchData = useCallback(
		(params: Partial<typeof reduxParams>) => {
			if (id || propsId) {
				const newParams = {
					...reduxParams,
					...params,
				};
				let convertRefTableName = convertRefTableNameToCallAPI(refTableName);

				dispatch(
					CommonActions.getUpdateHistoryListRequest({
						...newParams,
						refTableId: Number(id ?? propsId),
						refTableName: convertRefTableName,
						allData: false,
						fieldName: newParams?.fieldName,
						fieldArea: newParams?.fieldArea,
					})
				);
			}
		},
		[id, propsId, reduxParams, refTableName]
	);

	const onPageSizeChange = useCallback(
		(pageSize: DefaultOptionType['value']) => {
			fetchData({
				page: 1,
				pageSize: Number(pageSize),
			});
		},
		[fetchData]
	);

	const onPageChange = useCallback(
		(page: number) => {
			fetchData({
				page,
			});
		},
		[fetchData]
	);

	const columns = useMemo(
		() =>
			getColumns({
				historyUpdateOptionJson,
				updateAreaOptionsJson,
				t,
				autocompleteSuggestion,
				isForeignRole,
			}),
		[
			historyUpdateOptionJson,
			updateAreaOptionsJson,
			t,
			autocompleteSuggestion,
			isForeignRole,
		]
	);

	useEffect(() => {
		if (!open) {
			filterRef?.current?.onReset?.(true);
		}
	}, [open, filterRef]);

	return {
		initialValues,
		reduxParams,
		dataSource,
		total,
		loading,
		historyUpdateOptions,
		fetchData,
		onPageSizeChange,
		onPageChange,
		columns,
		filterRef,
		t,
		updateAreaOptions,
	};
};
